import {
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet, HelmetProvider } from 'react-helmet-async';

import { configureSpeech } from '@suntex.fim/a11y';
import { Client } from '@suntex.fim/api-client';
import { AuthService } from '@suntex.fim/auth';
import { AuthProvider } from '@suntex.fim/auth/react';
import { Locale } from '@suntex.fim/enums';
import {
  ThemeProvider,
  dismissAllMessages,
  showErrorMessage,
} from '@suntex.fim/ui';
import { currentFimYear, previousFimYear } from '@suntex.fim/utils';

import { initAxiosAuth } from '../axios';
import { FrameworkContext } from '../context';
import { useSessionExpiration } from '../hooks/useSessionExpiration';

import { ClassicDomainProvider } from './ClassicDomainProvider';
import { HomepageRedirectProvider } from './HomepageRedirectProvider';
import { JwtUrlLogin } from './JwtUrlLogin';
import { SessionExpiredModal } from './SessionExpiredModal';
import { SoundProvider } from './SoundProvider';

const FIVE_MINUTES_MS = 300_000;
const TEN_MINUTES_MS = 600_000;

// authService is effectively constant — it's memoized by a stable apiBaseURL
// and will never change during the lifetime of the QueryClient.
function createDefaultQueryClient(authService: AuthService) {
  return new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => {
        const { signingOut, isAuthenticated } = authService.getState();
        const description = (query.queryKey as string[]).join('/') || 'data';
        if (signingOut || !isAuthenticated) {
          // eslint-disable-next-line no-console
          console.warn(
            `Suppressed error toast: Failed to load ${description}.`,
            error,
          );
          return;
        }
        showErrorMessage(`Failed to load ${description}.`, error);
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: FIVE_MINUTES_MS,
        gcTime: TEN_MINUTES_MS,
        refetchOnWindowFocus: false,
        retry: 2,
      },
    },
  });
}

/**
 * FrameworkProvider that initializes axios auth internally.
 * Use this when you want the provider to manage the API client lifecycle.
 */
export function FrameworkProvider({
  children,
  apiBaseURL,
  onSoundToggle,
  setupInterceptors,
}: {
  children: ReactNode;
  apiBaseURL: string;
  onSoundToggle?: (disabled: boolean) => void;
  /**
   * Optional hook called once after the axios instance is created.
   * Use this to register app-level response interceptors (e.g. error toasts).
   */
  setupInterceptors?: (axiosInstance: AxiosInstance) => void;
}) {
  const isIndia = useIsIndia();
  // Held in a ref so the memo only re-runs when apiBaseURL changes, not when
  // the caller passes a new function reference.
  const setupInterceptorsRef = useRef(setupInterceptors);
  setupInterceptorsRef.current = setupInterceptors;
  const { apiClient, authService, axiosInstance } = useMemo(() => {
    const result = initAxiosAuth(apiBaseURL);
    setupInterceptorsRef.current?.(result.axiosInstance);
    // Point speech synthesis at the authenticated client so it can fetch and
    // rotate short-lived Cognitive Services tokens.
    configureSpeech({
      fetchToken: () => result.apiClient.get_Speech_Token({}),
    });
    return result;
  }, [apiBaseURL]);
  const [queryClient] = useState(() => createDefaultQueryClient(authService));
  const calendar = useCalendar(isIndia);

  return (
    <FrameworkContext.Provider
      value={{
        api: apiClient,
        calendar,
        locale: isIndia ? Locale.IN : Locale.US,
        http: axiosInstance,
      }}
    >
      <HelmetProvider>
        <Helmet
          defaultTitle="First In Math"
          titleTemplate="First In Math » %s"
        />
        <QueryClientProvider client={queryClient}>
          <AuthProvider authService={authService}>
            <SessionGuard authService={authService} />
            <HomepageRedirectProvider>
              <JwtUrlLogin authService={authService}>
                <ThemeProvider>
                  <SoundProvider onSoundToggle={onSoundToggle}>
                    <ClassicDomainProvider>{children}</ClassicDomainProvider>
                  </SoundProvider>
                </ThemeProvider>
              </JwtUrlLogin>
            </HomepageRedirectProvider>
          </AuthProvider>
        </QueryClientProvider>
      </HelmetProvider>
    </FrameworkContext.Provider>
  );
}

export function FrameworkProviderForClassic({
  children,
  api,
  auth,
  locale,
  onSoundToggle,
  initialSoundDisabled,
}: {
  children: ReactNode;
  api: Client;
  auth: AuthService;
  locale: Locale;
  onSoundToggle?: (disabled: boolean) => void;
  initialSoundDisabled?: boolean;
}) {
  const isIndia = locale === Locale.IN;
  const [queryClient] = useState(() => createDefaultQueryClient(auth));
  const calendar = useCalendar(isIndia);

  return (
    <FrameworkContext.Provider
      value={{
        api,
        calendar,
        locale: isIndia ? Locale.IN : Locale.US,
        http: api['instance'],
      }}
    >
      <HelmetProvider context={{}}>
        <QueryClientProvider client={queryClient}>
          <AuthProvider authService={auth}>
            <ThemeProvider theme="classic">
              <SoundProvider
                onSoundToggle={onSoundToggle}
                initialSoundDisabled={initialSoundDisabled}
              >
                {children}
              </SoundProvider>
            </ThemeProvider>
          </AuthProvider>
        </QueryClientProvider>
      </HelmetProvider>
    </FrameworkContext.Provider>
  );
}

/**
 * Hook to create calendar utilities based on locale
 */
function useCalendar(isIndia: boolean) {
  return useMemo(
    () => ({
      getCurrentSeason: () => {
        const now = new Date();
        return currentFimYear({ now, isIndia });
      },
      getPreviousSeason: () => {
        const now = new Date();
        return previousFimYear({ now, isIndia });
      },
    }),
    [isIndia],
  );
}

/**
 * Hook to detect if the hostname indicates India locale
 */
function useIsIndia() {
  const hostname =
    typeof window !== 'undefined' ? window.location.hostname : '';
  return hostname.endsWith('.in');
}

/**
 * Runs session expiration checks and renders the session-expired modal.
 * Must be rendered inside AuthProvider so it can access auth state.
 */
function SessionGuard({ authService }: { authService: AuthService }) {
  useSessionExpiration(authService);

  useEffect(() => {
    return authService.subscribe((state) => {
      if (state.signingOut) {
        dismissAllMessages();
      }
    });
  }, [authService]);

  return <SessionExpiredModal />;
}
